import React, { createContext, useEffect, useReducer } from 'react';

// third-party
import jwtDecode from 'jwt-decode';

// reducer - state management
import { LOGIN, LOGOUT } from 'store/reducers/actions';
import authReducer from 'store/reducers/auth';

// project import
import Loader from 'components/Loader';
import axios from 'utils/axios';
import { KeyedObject } from 'types/root';
import { AuthProps, UserContextType, IdentityServerAuthRequest, JwtTokenModel, UserProfile, JwtUserProfile } from 'types/auth';
import { authenticateUser, refreshTokens } from 'components/authentication/api/authentication-api';
import PriceListSubscriptionAPI from 'api/pricelist-subscription';
import { Everest } from 'common/constants';
import DealerConsentAPI from 'api/dealer-consent';


// constant
const initialState: AuthProps = {
  isLoggedIn: false,
  isInitialized: false,
  user: null
};

// =============================
// Verify Token
// =============================
const verifyToken: (st: string) => boolean = (serviceToken) => {
  if (!serviceToken) {
    return false;
  }

  const decoded: KeyedObject = jwtDecode(serviceToken);
  /**
   * Property 'exp' does not exist on type '<T = unknown>(token: string, options?: JwtDecodeOptions | undefined) => T'.
   */
  return decoded.exp > Date.now() / 1000;
};

// =============================
// Set Session
// =============================
const setSession = (accessToken?: string | null, refreshToken?: string | null) => {
  if (refreshToken) {
    localStorage.setItem('dealer-portal-refreshToken', refreshToken);
  }
  if (accessToken) {
    localStorage.setItem('dealer-portal-accessToken', accessToken);
    axios.defaults.headers.common.Authorization = `Bearer ${accessToken}`;
  } else {
    localStorage.removeItem('dealer-portal-showHelp');
    localStorage.removeItem('dealer-portal-accessToken');
    localStorage.removeItem('dealer-portal-refreshToken');
    delete axios.defaults.headers.common.Authorization;
  }
};

// ==============================|| USER CONTEXT & PROVIDER ||============================== //

const UserContext = createContext<UserContextType | null>(null);

const LoadUser = async (
  accessToken: string,
  calledFrom: string,
  dispatch: (arg0: { type: string; payload: { isLoggedIn: boolean; user: UserProfile } }) => void
): Promise<any> => {
  if (accessToken === '') throw new Error('Invalid Access Token');

  const decodedToken: JwtTokenModel = jwtDecode(accessToken);

  let caseFixProfile = decodedToken.profile.replace('"DmsDealerId"', '"dmsDealerId"');
  caseFixProfile = caseFixProfile.replace('"DealerId"', '"dealerId"');
  caseFixProfile = caseFixProfile.replace('"UserId"', '"userId"');

  decodedToken.profile = caseFixProfile;

  const decodedProfile: JwtUserProfile = JSON.parse(decodedToken.profile);
  const user: UserProfile = {
    name: decodedToken.name,
    dealerId: decodedProfile.dealerId,
    dmsDealerId: decodedProfile.dmsDealerId,
    userId: decodedProfile.userId,
    givenName: decodedToken.given_name,
    dmsType: 'unknown',
    dmsCompany: 'unknown',
    isPriceListSubscriptionApproved: false,
    priceListSubscriptionId: 0,
    inGracePeriod: false,
    gracePeriodDays: 0,
    isLegacyDealer: false,
    newDealerStatus: 'C',      // NewDealer
    showPortalView2024: false
  };

  console.log('LU');

  try {
    // Determine portal mode
    const path = window.location.pathname.toLowerCase();
    const isDealerConsentPortal = path.startsWith('/dealerconsent');

    // -----------------------------
    // PRICE LIST PORTAL ONLY:
    // Enforce subscription rules
    // -----------------------------
    if (!isDealerConsentPortal) {
      const response = await PriceListSubscriptionAPI.getSubscriptionClientByDealerId(decodedProfile.dealerId);

      if (!!!response) throw new Error(`No response from API (loaduser)[${decodedProfile.dealerId}]`);
      if (!!!response.result) {
        if (!!!response.errorMessage) throw new Error('Invalid Login [99]');
        if (response.errorMessage.toLowerCase().includes('dealer not found'))
          throw new Error(`Invalid Login [00] - Dealer Not Found [${decodedProfile.dealerId}]`);
        else if (response.errorMessage.toLowerCase().includes('subscription not found'))
          throw new Error('Invalid Login [05] - Subscription Not Found');
        else if (response.errorMessage.toLowerCase().includes('invalid dealer id'))
          throw new Error(`Invalid Login [04] [${decodedProfile.dealerId}]`);
        else
          throw new Error(`No result in response from API (loaduser)[${decodedProfile.dealerId}]`);
      }

      if (response.statusCode === 'Successful') {
        if (response.result.priceListSubscriptionId <= 0) throw new Error('Invalid Login [01]');
        else if (!!!response.result.isActive) throw new Error('Invalid Login [02]');
        else if (!!!response.result.isSubscriptionActive) throw new Error('Invalid Login [03]');

        console.log('✅', response.result.dealerId, '|', response.result.subscriptionId);

        user.dmsType = response.result.dmsType;
        user.dmsCompany = response.result.dmsCompany;
        user.address = response.result.address;
        user.city = response.result.city;
        user.email = response.result.email;
        user.state = response.result.stateOrProvince;
        user.companyName = response.result.subscriptionClientName;
        user.priceListSubscriptionId = response.result.subscriptionId;
        user.isPriceListSubscriptionApproved = response.result.isApproved ?? false;
        user.isLegacyDealer = response.result.isLegacyDealer ?? false;
        user.inGracePeriod = response.result.inGracePeriod ?? false;
        user.gracePeriodDays = response.result.gracePeriodDays ?? 0;
        user.newDealerStatus = response.result.newDealerStatus ?? 'C';

        const currentDate: Date = new Date();
        const newPortalDateString: string = process.env.REACT_APP_PORTALVIEW2024 ?? '2024-01-01';
        const newPortalDate: Date = new Date(newPortalDateString);
        user.showPortalView2024 = currentDate > newPortalDate;

        PriceListSubscriptionAPI.addApplicationLogEntry(
          `Login: [${user.dmsCompany}] [${user.dealerId}] [${user.companyName}]`,
          'Information',
          user.name ?? 'Unknown'
        );
      } else {
        console.log('❌', response.result);
        throw new Error('Invalid Login');
      }
    } else {
      // Dealer Consent portal: skip subscription validation entirely
      console.log('Dealer Consent login: skipping PriceListSubscriptionClient subscription checks');
    }

    // ----------------------------------------------------------
    // DEALER CONSENT (safe to run for both portals; storage only matters for consent flow)
    // ----------------------------------------------------------
    try {
      const consentDealerInfo = await DealerConsentAPI.getDealerInformationByDealerId(decodedProfile.dealerId);

      if (consentDealerInfo && String(consentDealerInfo.systemId) !== '-1') {
        localStorage.setItem('dealer-portal-consentDealerInfo', JSON.stringify(consentDealerInfo));
        console.log('✅ Stored dealer-portal-consentDealerInfo', consentDealerInfo.systemId, consentDealerInfo.dmsType);
      } else {
        localStorage.removeItem('dealer-portal-consentDealerInfo');
        console.warn('⚠️ Consent dealer identity not resolved (systemId = -1)', consentDealerInfo);
      }
    } catch (e) {
      localStorage.removeItem('dealer-portal-consentDealerInfo');
      console.error('❌ Failed to resolve/store consent dealer identity by dealerId', e);
    }

    // Always log the user in once we've completed required portal initialization
    dispatch({
      type: LOGIN,
      payload: {
        isLoggedIn: true,
        user
      }
    });

    return;
  } catch (err) {
    console.log(`LoadUser Failed [LU].  ${err}`);
    setSession(null, null);
    dispatch({ type: LOGOUT, payload: { isLoggedIn: false, user } });
    throw new Error('Unable to login at this time [LU]');
  }

};

export const JWTProvider = ({ children }: { children: React.ReactElement }) => {
  const [state, dispatch] = useReducer(authReducer, initialState);
  // =============================
  // useEffect for JWTProvider
  // =============================
  useEffect(() => {
    const init = async () => {
      try {
        console.log('🔓🔓🔓 [UC->UE]');
        let accessToken: string = window.localStorage.getItem('dealer-portal-accessToken') ?? '';
        let refreshToken = window.localStorage.getItem('dealer-portal-refreshToken');

        if (!!!verifyToken(accessToken))
          console.log('  VT -> 💥');

        if (accessToken && (accessToken !== '') && verifyToken(accessToken)) {   // checks to see if token is valid and not expired          
          LoadUser(accessToken, 'useEffect init, token verified', dispatch);
        } else if (refreshToken) {
          console.log('🔄🔄🔄');
          refreshTokens(refreshToken)
            .then((response) => {
              console.log('✅✅✅');
              const { access_token, refresh_token /* , scope */ } = response.data;
              setSession(access_token, refresh_token);
              accessToken = access_token ?? '';
              refreshToken = refresh_token;
              LoadUser(accessToken, 'useEffect init, refreshtoken', dispatch);
            })
            .catch((reason) => {
              console.log('🛑🛑🛑', reason);
              logout();
            })
        } else {
          console.log('🛑🛑🛑 NT');
          logout();
        }
      } catch (err) {
        console.log(`🛑🛑🛑 Unable to login at this time. [JWT] ${err}`);
        logout();
      }
    };

    init();
  }, []);

  // ============================================================== //
  // Login
  // ============================================================== //
  const login = async (dmsDealerId: number, userName: string, password: string, token: string) => {
    const secret = process.env.REACT_APP_IDMS_CLIENT_SC || '';
    const request: IdentityServerAuthRequest = {
      DmsDealerId: dmsDealerId,
      Username: userName,
      Password: password,
      ProductId: process.env.REACT_APP_IDMS_PRODUCT_ID || '',
      ClientId: 'dealerPortal',
      ClientSecret: secret
    };

    let accessToken: string;

    // todo: these two sections are identical.  Waiting for changes to idms.

    if (token && token !== '') {
      try {
        const response = await authenticateUser(request);
        const { access_token, refresh_token /* , scope */ } = response.data;

        setSession(access_token, refresh_token);

        accessToken = access_token ?? '';
        return LoadUser(accessToken, 'login, token exists', dispatch);
      } catch {
        console.log('🗑');
        localStorage.clear();
        throw new Error('Invalid Login');
      }
    } else {
      try {
        await authenticateUser(request).then((response) => {
          const { access_token, refresh_token /* , scope */ } = response.data;
          setSession(access_token, refresh_token);

          accessToken = access_token ?? '';
          return LoadUser(accessToken, 'login, new token', dispatch);
        });
      } catch {
        console.log('🗑');
        localStorage.clear();
        throw new Error('Invalid Login');
      }
    }
  };

  const logout = () => {
    setSession(null, null);

    dispatch({ type: LOGOUT });
  };

  const updateProfile = () => {
    /* console.log('updateProfile'); */
  };

  if (state.isInitialized !== undefined && !state.isInitialized) {
    return <Loader />;
  }

  return <UserContext.Provider value={{ ...state, login, logout, updateProfile }}>{children}</UserContext.Provider>;
};

export default UserContext;
